import { HttpClient } from '@angular/common/http'
import { Component, Input } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Citation, CitationType } from '@cwan-gpt-ui/models'
import { environment } from 'environments/environment'
import {
  downloadGeneratedFile,
  transformS3UrlToDownloadUrl,
} from '../utils/file-download.utils'

@Component({
  selector: 'chat-message-citation',
  templateUrl: './chat-message-citation.component.html',
  styleUrls: [`./chat-message-citation.component.scss`],
})
export class ChatMessageCitationComponent {
  citationSourceUrl = ''
  citationNumber = 0
  citationTitle = ''
  textCitation = false
  
  isS3Url = false
  isGptWsUrl = false
  isFileDownloadLink = false
  downloadUrl = ''
  
  private gptWsBaseApi = environment.cwanGptWsBaseUrl
  
  constructor(
    private httpClient: HttpClient,
    private snackbar: MatSnackBar
  ) {}
  
  @Input() set citation(value: Citation) {
    this.citationSourceUrl = value.source
    this.citationTitle = value.title || ''
    this.textCitation = value.type === CitationType.TEXT
    
    // Determine URL types for proper handling
    this.isS3Url = value.source.includes('s3.amazonaws.com')
    this.isGptWsUrl = value.source.includes(this.gptWsBaseApi)
    
    // Determine the download URL:
    // - If it's an S3 URL, transform it to our secure download endpoint
    // - If it's already a gpt-ws URL, use it directly
    this.downloadUrl = this.citationSourceUrl
    if (this.isS3Url) {
      this.downloadUrl = transformS3UrlToDownloadUrl(
        this.citationSourceUrl,
        this.gptWsBaseApi
      )
    }
    
    // Determine if this is a file download link (either S3 or from our API)
    this.isFileDownloadLink = this.isS3Url || this.isGptWsUrl
  }
  
  @Input() set index(i: number) {
    this.citationNumber = i + 1
  }
  
  async handleDownload(): Promise<void> {
    if (!this.isFileDownloadLink) {
      return
    }
    
    try {
      await downloadGeneratedFile(this.httpClient, {
        filename: this.downloadUrl,
        originalSource: this.citationSourceUrl, // Pass the original source URL for fallback
        onError: (message) => {
          console.error('Download error:', message)
          this.snackbar.open(message, 'Close', {
            duration: 5000,
            panelClass: 'error-snackbar',
            horizontalPosition: 'right',
            verticalPosition: 'top'
          })
        },
      })
    } catch (error) {
      const errorMessage = (error as Error).message || 'Error downloading the file'
      console.error('Error downloading the file:', errorMessage)
      this.snackbar.open(errorMessage, 'Close', {
        duration: 5000,
        panelClass: 'error-snackbar',
        horizontalPosition: 'right',
        verticalPosition: 'top'
      })
    }
  }
  
  handleClick(event: Event): void {
    if (this.textCitation) {
      // Text citations don't have click behavior
      return
    }
    
    if (this.isFileDownloadLink) {
      // Prevent default link behavior and handle download instead
      event.preventDefault()
      this.handleDownload()
    }
    // For regular URLs, let the default link behavior handle opening in new tab
  }
}
